import type { CSSProperties, MouseEvent, ReactElement } from 'react';
import type { GroupBase, MultiValueProps, StylesConfig, ValueContainerProps } from 'react-select';
import { components } from 'react-select';
import styled from '@emotion/styled';
import { useAppTheming } from '@trinity/chat-components/containers/AppTheming/index.js';
import type { NamedItem } from './CollapsedValueContainer';
import * as Presentation from './Presentation';

// Shared side-panel pill pieces. Used by the Tag and Project filters so multi-selects in the
// side panel truncate long labels with an ellipsis and let each selected item be removed.

/** White-label colours for a pill, exposed as the CSS custom properties `PillItem` reads. */
export function useChipStyles(): CSSProperties | undefined {
  const theming = useAppTheming();

  if (!theming.enabled || !theming.palette.primary) {
    return undefined;
  }

  return {
    '--chip-bg': theming.palette.primary.lighter,
    '--chip-border': theming.palette.primary.main,
    '--chip-color': theming.palette.primary.main,
  } as CSSProperties;
}

/** Row value cell that may shrink below its content so long labels truncate instead of overflowing. */
export const ShrinkableValue = styled(Presentation.Value)({
  minWidth: 0,
  overflow: 'hidden',
});

export const PillContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginBottom: '6px',
  width: '100%',
  minWidth: 0,
});

// Font size/family match Presentation.buildSelectStyles (used by Published Date,
// Project, and Record Type filters) so all Side Panel filter values render consistently.
export const PillItem = styled.div(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  borderRadius: '16px',
  backgroundColor: 'var(--chip-bg, #f0f0f0)',
  border: '1px solid var(--chip-border, #ccc)',
  color: 'var(--chip-color, #333)',
  fontSize: '14px',
  fontFamily: theme.fonts.regular,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  minWidth: 0,
  maxWidth: '100%',
}));

export const PillLabel = styled.span({
  flex: '0 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

export const PillRemoveButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '18px',
  height: '18px',
  border: 'none',
  backgroundColor: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 'bold',
  lineHeight: 1,
  padding: '0 2px',
  margin: 0,

  '&:hover': {
    opacity: 0.7,
  },
});

export const OptionLabelText = styled.span({
  flex: '0 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

/** Menu option label that truncates and shows the full text on hover. */
export function renderTruncatedOption(option: NamedItem): ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, pointerEvents: 'none' }}>
      <OptionLabelText title={option.name}>{option.name}</OptionLabelText>
    </div>
  );
}

/** Keeps the control, value area and menu inside the side panel's width. */
export function withSidePanelStyles<Option>(base: StylesConfig<Option, true>): StylesConfig<Option, true> {
  return {
    ...base,
    menu: (provided, props) => ({
      ...(base.menu ? base.menu(provided, props) : provided),
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
    }),
    menuPortal: (provided, props) => ({
      ...(base.menuPortal ? base.menuPortal(provided, props) : provided),
      boxSizing: 'border-box',
    }),
    option: (provided, props) => ({
      ...(base.option ? base.option(provided, props) : provided),
      boxSizing: 'border-box',
      maxWidth: '100%',
    }),
    control: (provided, props) => ({
      ...(base.control ? base.control(provided, props) : provided),
      boxSizing: 'border-box',
      maxWidth: '100%',
    }),
    valueContainer: (provided, props) => ({
      ...(base.valueContainer ? base.valueContainer(provided, props) : provided),
      boxSizing: 'border-box',
      minWidth: 0,
      maxWidth: '100%',
      overflowX: 'hidden',
    }),
  };
}

/**
 * Side-panel `ValueContainer`: wraps react-select's own `MultiValue` children (see
 * `SidePanelMultiValue`) in a wrapping pill row, so every selected item keeps its remove button.
 */
export function SidePanelPillValueContainer<T extends NamedItem>(
  props: ValueContainerProps<T, true, GroupBase<T>>
): ReactElement {
  const { children, getValue } = props;

  if (getValue().length === 0 || !Array.isArray(children)) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  // react-select passes [multiValueElements, input]
  const inputChild = children[children.length - 1];
  const pills = children.slice(0, -1);

  return (
    <components.ValueContainer {...props}>
      <PillContainer>{pills}</PillContainer>
      {inputChild}
    </components.ValueContainer>
  );
}

/** Side-panel `MultiValue`: truncating pill with a tooltip and a per-item remove button. */
export function SidePanelMultiValue<T extends NamedItem>(
  props: MultiValueProps<T, true, GroupBase<T>>
): ReactElement {
  const chipStyles = useChipStyles();
  const { data, isDisabled, removeProps } = props;

  // mousedown must not open the menu or move focus; removal itself runs on click so it also
  // works from the keyboard (Enter/Space on the focused button fire click).
  const handleMouseDown = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemove = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    // @ts-expect-error react-select type incompatibility between div and button click handlers
    removeProps.onClick?.(e);
  };

  return (
    <PillItem style={chipStyles} title={data.name}>
      <PillLabel>{data.name}</PillLabel>
      {!isDisabled && (
        <PillRemoveButton
          type="button"
          onMouseDown={handleMouseDown}
          onClick={handleRemove}
          aria-label={`Remove ${data.name}`}
        >
          ×
        </PillRemoveButton>
      )}
    </PillItem>
  );
}
